import { trackEvent } from "src/Amplitude";
import { ArrowBackIos } from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  FormControlLabel,
  Grid,
  IconButton,
  Modal,
  Paper,
  Radio,
  RadioGroup,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import Reports from "src/pages/Reports";
import api from "src/store/features/api";
import { dateFormatter } from "src/utils/helpers/DateAndTimeHelpers";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import { getUserTimeZone } from "src/utils/hooks/useTimeZone";
import {
  handleChangeStartDate,
  mobileCategories,
  modalDisclaimer,
  summaryItems,
  timeRangeOptions,
} from "./ReportConst";
import { TransactionsSummaryDeskTopStyles } from "./styles/TransactionsSummaryDeskTopStyles.styles";
import TransactionDateRangePicker from "./TransactionDateRangePicker";
import { VerificationBanner } from "../core/VerificationBanner";
import usePermission from "src/utils/hooks/usePermission";
interface transactionReportsProps {
  state: boolean;
  toggleExportModal: React.DispatchWithoutAction;
  reportsFilter: any;
  setReportsFilter: any;
}

export default function TransactionReports(props: transactionReportsProps) {
  const [trigger] = api.useLazyExportCsvQuery();
  const classes = TransactionsSummaryDeskTopStyles();
  const { state, toggleExportModal, reportsFilter, setReportsFilter } = props;
  // Account Summary type state
  const [category, setCategory] = useState("Account Summary");
  const [startDate, setStartDate] = useState<number | undefined>(undefined);
  const [endDate, setEndDate] = useState<undefined | number>(undefined);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const isMobile = useIsMobile();
  // Select Time Period State
  const [timeRange, setTimeRange] = useState("All time");
  useEffect(() => {
    setErrorMessage("");
    if (timeRange) {
      setStartDate(handleChangeStartDate(timeRange));
      setEndDate(undefined);
      reportsFilter.endDate = undefined;
      reportsFilter.startDate = undefined;
    }
    if (reportsFilter.endDate) {
      setEndDate(reportsFilter.endDate);
      setStartDate(reportsFilter.startDate);
    }
  }, [timeRange, reportsFilter.startDate, reportsFilter.endDate]);

  useEffect(() => {
    // Reset error message everytime component is closed
    if (!state) {
      setErrorMessage("");
      setTimeRange("All time");
    }
  }, [state]);

  const exportCSV = () => {
    trigger({ timezone: getUserTimeZone, startDate, endDate })
      .then((fullfilled) => {
        if (fullfilled.data) {
          const url = window.URL.createObjectURL(new Blob([fullfilled.data.toString()]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `transactions_${new Date()}.csv`);
          document.body.appendChild(link);
          link.click();
        }
      })
      .catch((err) => console.error("ERROR: Download Transactions Summary CSV " + err));
    trackEvent(
      "Download Transactions Summary CSV",
      { timezone: getUserTimeZone, startDate, endDate },
      "Export Report",
    );
  };

  const handleClick = (option: string) => {
    if (option !== null) {
      if (option === "Custom") {
        setOpenCalendar(true);
      } else {
        setTimeRange(option);
      }
      trackEvent(
        `Changed timeframe`,
        {
          option,
        },
        "Export Report",
      );
    }
  };

  const { userPermission } = usePermission();
  const MESSAGE =
    "Export Account Reports a powerful feature for verified customers. Analyze data effortlessly and make informed decisions for your account. Verify your account to unlock this game-changing tool.";

  return (
    <>
      {userPermission === "guest" && (
        <VerificationBanner open={state} onClose={toggleExportModal} message={MESSAGE} />
      )}
      {userPermission !== "guest" && (
        <Modal open={state} onClose={toggleExportModal}>
          <Paper elevation={0} className={classes.paperContainer}>
            <Grid container item className={classes.titleGrid}>
              {isMobile && (
                <Grid item xs={2}>
                  <IconButton
                    onClick={toggleExportModal}
                    sx={{ color: "#FFF", paddingLeft: "26px" }}>
                    <ArrowBackIos />
                  </IconButton>
                </Grid>
              )}
              <Grid item xs={isMobile ? 8 : 12}>
                <Typography className={classes.title}>Export Report</Typography>
              </Grid>
              {isMobile && <Grid item xs={2}></Grid>}
            </Grid>
            <Box className={classes.exportBox}>
              <Typography
                sx={{
                  fontSize: { xs: "14px" },
                  fontWeight: { xs: "600" },
                  color: { xs: "#5C6469" },
                  marginBottom: { xs: "10px" },
                }}>
                Select Summary Type
              </Typography>
              {isMobile ? (
                <div style={{ marginBottom: "10px" }}>
                  {mobileCategories.map((option, iterator) => (
                    <ToggleButtonGroup
                      onChange={() => setCategory(option)}
                      orientation="horizontal"
                      value={category}
                      exclusive
                      key={iterator}>
                      <ToggleButton className={classes.toggleButton} value={option}>
                        <Typography sx={{ fontSize: "14px" }}>{option}</Typography>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  ))}
                </div>
              ) : (
                <RadioGroup
                  row
                  value={category}
                  className={classes.radioGroup}
                  onChange={(e) => setCategory(e.target.value)}>
                  <>
                    {summaryItems.map((item, iterator) => (
                      <ButtonBase
                        className={clsx(classes.card, {
                          [classes.cardSelected]: category === item.name,
                        })}
                        onClick={() => {
                          setCategory(item.name);
                          trackEvent(
                            `Changed Summary Type`,
                            {
                              summaryType: item.name,
                            },
                            "Export Summary",
                          );
                        }}
                        key={iterator}>
                        <Card elevation={0} sx={{ background: "transparent" }}>
                          <FormControlLabel
                            sx={{ margin: "0px" }}
                            id="account"
                            value={item.name}
                            control={
                              <Radio
                                sx={{
                                  padding: "9px 5px 9px 0px",
                                  "& .MuiSvgIcon-root": {
                                    fontSize: 18,
                                  },
                                }}
                              />
                            }
                            label={
                              <span className={classes.categoryTitle}>
                                <>
                                  {item.name.includes("Account")
                                    ? item.name + " PDF"
                                    : item.name + " CSV"}
                                </>
                              </span>
                            }
                          />
                          <>
                            {item.listItems.map((el, iterator) => (
                              <Typography className={classes.cardtypography} key={iterator}>
                                <span>{el}</span>
                              </Typography>
                            ))}
                          </>
                        </Card>
                      </ButtonBase>
                    ))}
                  </>
                </RadioGroup>
              )}
              {isMobile && (
                <Grid
                  container
                  sx={{
                    textAlign: "center",
                    background: "#F5F3EF",
                    padding: "15px",
                    borderRadius: "10px",
                    justifyContent: "center",
                  }}>
                  <Typography
                    sx={{
                      textDecoration: "underline",
                      fontSize: "14px",
                      marginBottom: "10px",
                    }}>
                    What's included
                  </Typography>
                  <div style={{ marginBottom: "10px" }}>
                    {summaryItems[category.includes("Account") ? 0 : 1].listItems.map(
                      (item, iterator) => (
                        <span
                          style={{
                            color: "#7B858B",
                            fontSize: "14px",
                            marginRight: "10px",
                          }}
                          key={iterator}>
                          {item}
                        </span>
                      ),
                    )}
                  </div>
                </Grid>
              )}
              <Typography
                sx={{
                  marginTop: "30px",
                  fontWeight: { xs: "600" },
                  color: { xs: "#5C6469" },
                  fontSize: { xs: "14px" },
                }}>
                Select Time Period
              </Typography>
              <Grid item className={classes.toggleButtonGroup}>
                {timeRangeOptions.map((option, iterator) => (
                  <ToggleButtonGroup
                    onChange={() => handleClick(option)}
                    orientation="horizontal"
                    value={timeRange}
                    exclusive
                    key={iterator}>
                    <ToggleButton className={classes.toggleButton} value={option}>
                      {option}
                    </ToggleButton>
                  </ToggleButtonGroup>
                ))}
              </Grid>
              {/* Date filter field */}
              <Grid
                container
                className={classes.gridContainerdatePicker}
                onClick={() => setTimeRange("")}>
                <Grid
                  item
                  xs={10}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}>
                  {startDate && (
                    <Typography sx={{ fontSize: { xs: "14px" } }}>
                      {dateFormatter(new Date(startDate))}
                    </Typography>
                  )}
                  {!startDate && (
                    <Typography sx={{ fontSize: { xs: "14px" } }}>1 August 2020</Typography>
                  )}
                  <Typography sx={{ margin: "0px 5px", fontSize: { xs: "14px" } }}>to</Typography>
                  {endDate && (
                    <Typography sx={{ fontSize: { xs: "14px" } }}>
                      {dateFormatter(new Date(endDate))}
                    </Typography>
                  )}
                  {!endDate && <Typography sx={{ fontSize: { xs: "14px" } }}>Today</Typography>}
                </Grid>
                {/* Picker icon */}
                <Grid item xs={2} sx={{ paddingLeft: "30px" }}>
                  <TransactionDateRangePicker
                    setOpenCalendar={setOpenCalendar}
                    openCalendar={openCalendar}
                    minimalIcon
                    setReportsFilter={setReportsFilter}
                    filterData={reportsFilter}
                  />
                </Grid>
              </Grid>
              {/* Error Message */}
              <Typography color={"red"} ml={1}>
                {errorMessage}
              </Typography>

              {/* Cancel and Export */}
              <Typography
                sx={{
                  fontSize: "12px",
                  textAlign: "start",
                  marginTop: { xs: "16px" },
                }}>
                {modalDisclaimer}
              </Typography>
              <Grid container spacing={1} sx={{ marginTop: "20px" }}>
                <Grid item xs={6}>
                  <Button
                    disableRipple
                    variant="outlined"
                    fullWidth
                    sx={{
                      height: "40px",
                      border: "none",
                      textTransform: "capitalize",
                      background: "#F8F9F9",
                    }}
                    onClick={() => {
                      toggleExportModal();
                      trackEvent("Canceled Export Report", { category }, "Export Report");
                    }}>
                    <Typography fontWeight={"500"}>Cancel</Typography>
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  {category.includes("Transaction") && (
                    <Button disableRipple className={classes.exportButton} onClick={exportCSV}>
                      Export
                    </Button>
                  )}
                  {category.includes("Account") && (
                    <Reports
                      startDate={startDate}
                      endDate={endDate}
                      setErrorMessage={setErrorMessage}
                    />
                  )}
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Modal>
      )}
    </>
  );
}
